import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { supabase, supabaseAdmin } from '../utils/supabase';
import { utcToUkTime } from '../utils/dateUtils';
import {
  balanceGameWithBruteForce,
  savedTeamsMatchRoster,
} from '../components/admin/team-balancing/bruteForceOptimal/autoBalance';
import type { TeamAssignmentData } from '../components/admin/team-balancing/types';

interface UseTeamAnnouncementProps {
  game?: any;
  onGameUpdated: () => Promise<void>;
}

/** Selected player ids for a game */
async function loadSelectedPlayerIds(gameId: string): Promise<string[]> {
  const { data, error } = await supabaseAdmin
    .from('game_registrations')
    .select('player_id')
    .eq('game_id', gameId)
    .eq('status', 'selected');

  if (error) throw error;
  return (data ?? []).map((r: { player_id: string }) => r.player_id);
}

/** The saved balance for a game, or null */
async function loadSavedBalance(gameId: string): Promise<TeamAssignmentData | null> {
  const { data, error } = await supabaseAdmin
    .from('balanced_team_assignments')
    .select('team_assignments')
    .eq('game_id', gameId)
    .maybeSingle();

  if (error) throw error;
  return data?.team_assignments ?? null;
}

/**
 * The brute-force balancer reads position and attribute data that only
 * signed-in users can see, so logged-out tabs never balance.
 */
async function isSignedIn(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

/**
 * Hook to handle team announcements
 * Uses timezone-aware date handling for proper time comparison
 *
 * Teams always come from the brute-force optimal balancer:
 * - Before announcement time, any signed-in viewer's tab balances the game and
 *   saves it (when no saved balance matches the selected players), so the
 *   pg_cron `auto_announce_due_teams` job can announce on time with no tab open.
 * - At announcement time, a saved balance is used if it still matches the
 *   selected players; otherwise brute force runs again first.
 */
export const useTeamAnnouncement = (props?: UseTeamAnnouncementProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProcessingAnnouncement, setIsProcessingAnnouncement] = useState(false);
  const [hasAnnouncedTeams, setHasAnnouncedTeams] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [isTeamAnnouncementTime, setIsTeamAnnouncementTime] = useState(false);

  // Use refs for values that shouldn't trigger re-renders
  const processingRef = useRef(false);
  const errorCountRef = useRef(0);
  const preBalancedGameRef = useRef<string | null>(null);
  const MAX_RETRIES = 3;

  const acquireLock = async (gameId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabaseAdmin
        .from('team_announcement_locks')
        .insert({
          game_id: gameId,
          locked_at: new Date().toISOString(),
          locked_until: new Date(Date.now() + 30000).toISOString() // 30 second lock
        })
        .select()
        .single();

      return !error && !!data;
    } catch (error: any) {
      if (error.message?.includes('team_announcement_locks" does not exist')) {
        console.log('Team announcement locks table does not exist, skipping lock acquisition');
        return true;
      }
      console.error('Lock acquisition error:', error);
      return false;
    }
  };

  const releaseLock = async (gameId: string) => {
    try {
      await supabaseAdmin
        .from('team_announcement_locks')
        .delete()
        .eq('game_id', gameId);
    } catch (error: any) {
      if (!error.message?.includes('team_announcement_locks" does not exist')) {
        console.error('Lock release error:', error);
      }
    }
  };

  const checkIsTeamAnnouncementTime = useCallback(() => {
    if (!props?.game) return false;

    // Convert UTC timestamp to UK timezone for proper comparison
    const announcementTime = utcToUkTime(new Date(props.game.team_announcement_time));
    const isAfterAnnouncementTime = currentTime > announcementTime;
    const isCorrectStatus = props.game.status === 'players_announced';

    return isAfterAnnouncementTime && isCorrectStatus;
  }, [props?.game, currentTime]);

  const handleTeamAnnouncement = useCallback(async () => {
    // Use ref to prevent multiple simultaneous executions
    if (!props?.game || processingRef.current) return;

    processingRef.current = true;
    setIsProcessingAnnouncement(true);
    let lockAcquired = false;

    try {
      // Logged-out tabs can't balance properly; leave it to the cron job or a signed-in tab
      if (!(await isSignedIn())) {
        setHasAnnouncedTeams(true);
        return;
      }

      // Try to acquire lock
      lockAcquired = await acquireLock(props.game.id);
      if (!lockAcquired) {
        console.log('Another instance is processing team announcement');
        return;
      }

      // Check current game status
      const { data: currentGame } = await supabaseAdmin
        .from('games')
        .select('status')
        .eq('id', props.game.id)
        .single();

      if (currentGame?.status !== 'players_announced') {
        console.log('Game is no longer in players_announced status');
        setHasAnnouncedTeams(true);
        return;
      }

      const { id } = props.game;
      const playerIds = await loadSelectedPlayerIds(id);

      if (playerIds.length === 0) {
        throw new Error('No selected players found');
      }

      // Use the saved balance if it still covers exactly the selected players,
      // otherwise run brute force now (dropouts/reserves since it was saved)
      let teamAssignments = await loadSavedBalance(id);
      let blueTeam: string[];
      let orangeTeam: string[];

      if (teamAssignments && savedTeamsMatchRoster(teamAssignments.teams, playerIds)) {
        console.log('Saved teams are valid, using pre-planned team assignments');
        blueTeam = teamAssignments.teams.filter((t) => t.team === 'blue').map((t) => t.player_id);
        orangeTeam = teamAssignments.teams.filter((t) => t.team === 'orange').map((t) => t.player_id);
      } else {
        console.log('No saved teams for the current players, running brute-force balancer');
        ({ blueTeam, orangeTeam, teamAssignments } = await balanceGameWithBruteForce(id, playerIds));
      }

      const { blue, orange } = teamAssignments.stats;

      // Call the stored procedure to update everything atomically
      const { data: result, error: updateError } = await supabaseAdmin.rpc(
        'update_team_assignments',
        {
          p_game_id: id,
          p_blue_team: blueTeam,
          p_orange_team: orangeTeam,
          p_team_assignments: teamAssignments,
          p_total_differential: Math.abs(blue.avgRating - orange.avgRating),
          p_attack_differential: blue.avgAttack - orange.avgAttack,
          p_defense_differential: blue.avgDefense - orange.avgDefense
        }
      );

      if (updateError) throw updateError;

      if (!result) {
        throw new Error('Team assignment update failed');
      }

      await props.onGameUpdated();

      // Use setTimeout to avoid state updates during render
      setTimeout(() => {
        toast.success('Teams have been announced');
        setHasAnnouncedTeams(true);
        errorCountRef.current = 0;
        setErrorCount(0);
      }, 0);

    } catch (error: any) {
      console.error('Error announcing teams:', error);

      // Use setTimeout to avoid state updates during render
      setTimeout(() => {
        errorCountRef.current += 1;
        setErrorCount(prev => {
          const newCount = prev + 1;
          if (newCount >= MAX_RETRIES) {
            setHasAnnouncedTeams(true);
            toast.error('Failed to announce teams after multiple attempts. Please contact an admin.');
          } else {
            toast.error(`Failed to announce teams (attempt ${newCount}/${MAX_RETRIES})`);
          }
          return newCount;
        });
      }, 0);
    } finally {
      if (lockAcquired && props.game) {
        await releaseLock(props.game.id);
      }
      processingRef.current = false;
      setIsProcessingAnnouncement(false);
    }
  }, [props, MAX_RETRIES]);

  // Pre-balance before announcement time so a saved brute-force balance exists
  // for the cron job. Runs once per game per tab; skips when the saved balance
  // already matches the selected players (e.g. an admin's hand-tuned save).
  useEffect(() => {
    const game = props?.game;
    if (!game || game.status !== 'players_announced' || isTeamAnnouncementTime) return;
    if (preBalancedGameRef.current === game.id) return;
    preBalancedGameRef.current = game.id;

    (async () => {
      try {
        if (!(await isSignedIn())) return;

        const playerIds = await loadSelectedPlayerIds(game.id);
        if (playerIds.length < 2) return;

        const saved = await loadSavedBalance(game.id);
        if (saved && savedTeamsMatchRoster(saved.teams, playerIds)) return;

        console.log('Pre-balancing teams with the brute-force balancer');
        await balanceGameWithBruteForce(game.id, playerIds);
      } catch (error) {
        // Not fatal: announcement time will balance again
        console.error('Error pre-balancing teams:', error);
      }
    })();
  }, [props?.game, isTeamAnnouncementTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setIsTeamAnnouncementTime(checkIsTeamAnnouncementTime());
  }, [checkIsTeamAnnouncementTime]);

  useEffect(() => {
    if (!props?.game) return;

    const checkAnnouncementTime = () => {
      const shouldAnnounceTeams =
        isTeamAnnouncementTime &&
        !processingRef.current &&
        !hasAnnouncedTeams &&
        errorCountRef.current < MAX_RETRIES;

      if (shouldAnnounceTeams) {
        handleTeamAnnouncement();
      }
    };

    // Initial check
    checkAnnouncementTime();

    // Set up polling with random offset to prevent thundering herd
    const randomOffset = Math.random() * 3000; // Random delay between 0-3 seconds
    const intervalId = setInterval(checkAnnouncementTime, 5000 + randomOffset);

    return () => clearInterval(intervalId);
  }, [props?.game, isTeamAnnouncementTime, hasAnnouncedTeams, handleTeamAnnouncement]);

  return {
    isTeamAnnouncementTime,
    isProcessingAnnouncement,
    hasAnnouncedTeams,
    errorCount
  };
};
